import type { AssignablePerson, CompanyRole, Grant } from '../lib/types';
import { initials } from '../pages/BoardCard';

/** Beyond three, a row of avatars stops being read and starts being a smudge. */
const SHOWN = 3;

/**
 * Whose side of the account somebody is on.
 *
 * `client` and `client_admin` are the customer's; everyone else is ours. Kept as one function
 * because the chips and the picker have to agree — a name shown red in the list and blue once
 * chosen would be worse than no colour at all.
 */
export function sideOf(role: CompanyRole | undefined): 'ours' | 'theirs' {
  return role === 'client' || role === 'client_admin' ? 'theirs' : 'ours';
}

export type Assignee = { id: string; name: string };

/**
 * Who is on a task, at a glance. Several names is the normal case now, so the space has to
 * hold more than one without the row growing.
 */
export function AssigneeStack({ people, size }: { people: Assignee[]; size?: 'sm' }) {
  if (people.length === 0) {
    return (
      <span className="dim" style={{ fontSize: 11 }}>
        unassigned
      </span>
    );
  }

  const shown = people.slice(0, SHOWN);
  const rest = people.length - shown.length;

  return (
    <span className="avatar-stack" title={people.map((person) => person.name).join(', ')}>
      {shown.map((person) => (
        <span key={person.id} className={size === 'sm' ? 'avatar avatar-sm' : 'avatar'}>
          {initials(person.name)}
        </span>
      ))}
      {rest > 0 && <span className={size === 'sm' ? 'avatar avatar-sm avatar-more' : 'avatar avatar-more'}>+{rest}</span>}
    </span>
  );
}

/**
 * Adding and removing names, as chips plus a picker.
 *
 * Deliberately not a popover: the drawer and the table are both scroll containers, and an
 * absolutely-positioned menu inside one gets clipped — the trap the notification panel fell
 * into. Chips in normal flow cannot be clipped by anything.
 */
export function AssigneePicker({
  value,
  people,
  disabled,
  onChange,
  compact,
  grant = 'full',
}: {
  value: Assignee[];
  people: AssignablePerson[];
  disabled?: boolean;
  onChange: (next: Assignee[]) => void;
  compact?: boolean;
  /**
   * The viewer's `work.assign`. `own` is the customer's: they put their own people on and take
   * them off, and ours are not theirs to move either way.
   *
   * Defaults to `full` so a caller that has not thought about it gets the behaviour our own
   * staff have always had — the API is the thing that actually refuses, and this only decides
   * which controls are worth offering.
   */
  grant?: Grant;
}) {
  const chosen = new Set(value.map((person) => person.id));
  const available = people.filter((person) => !chosen.has(person.id));
  const roleOf = new Map(people.map((person) => [person.id, person.role]));
  const sideClass = (id: string) => (sideOf(roleOf.get(id)) === 'theirs' ? ' avatar-client' : '');

  /**
   * Ours are not the customer's to take off.
   *
   * Shown DISABLED rather than removed: the × vanishing from some chips and not others reads
   * as a rendering fault, where a greyed one with a reason on it reads as a rule. The API
   * refuses it regardless — this is about not offering a button whose only outcome is an
   * error message.
   */
  const mayMove = (id: string) => grant === 'full' || sideOf(roleOf.get(id)) === 'theirs';

  /* Grouped so the two sides are separated by more than a colour, which a select cannot
     always be trusted to render and which somebody colour-blind should not have to rely on. */
  const ours = available.filter((person) => sideOf(person.role) === 'ours');
  const theirs = available.filter((person) => sideOf(person.role) === 'theirs');
  /**
   * In a table cell the name is dropped only once there are several people, where it would
   * wrap the row. One name reads better written out than as two initials.
   */
  const initialsOnly = compact && value.length > 1;

  return (
    <span className="assignee-picker">
      {value.map((person) => (
        <span key={person.id} className="chip" title={person.name}>
          <span className={`avatar avatar-sm${sideClass(person.id)}`}>{initials(person.name)}</span>
          {!initialsOnly && <span className="chip-name">{person.name}</span>}
          {!disabled && (
            <button
              type="button"
              className="chip-remove"
              disabled={!mayMove(person.id)}
              title={
                mayMove(person.id)
                  ? `Take ${person.name} off this task`
                  : `${person.name} is on the Lucentra team — ask us to reassign this`
              }
              aria-label={
                mayMove(person.id)
                  ? `Take ${person.name} off this task`
                  : `${person.name} cannot be removed by you`
              }
              onClick={() => mayMove(person.id) && onChange(value.filter((other) => other.id !== person.id))}
            >
              ×
            </button>
          )}
        </span>
      ))}

      {!disabled && available.length > 0 && (
        <select
          className="cell-select assignee-add"
          // Always shows the prompt: it is an action, not a field holding a value.
          value=""
          onChange={(event) => {
            const person = people.find((candidate) => candidate.id === event.target.value);
            if (person) onChange([...value, { id: person.id, name: person.name }]);
          }}
        >
          <option value="">{value.length === 0 ? 'Assign someone…' : '+ Add'}</option>
          {ours.length > 0 && (
            <optgroup label="Lucentra">
              {ours.map((person) => (
                <option key={person.id} value={person.id} className="opt-ours" disabled={grant !== 'full'}>
                  {person.name}
                </option>
              ))}
            </optgroup>
          )}
          {theirs.length > 0 && (
            <optgroup label="Their people">
              {theirs.map((person) => (
                <option key={person.id} value={person.id} className="opt-client">
                  {person.name}
                </option>
              ))}
            </optgroup>
          )}
        </select>
      )}

      {disabled && value.length === 0 && <span className="dim">Unassigned</span>}
    </span>
  );
}
